import React from 'react';
import {
  Box,
  HeadingText,
  Card,
  TextAction,
  ParagraphText,
} from '@leagueplatform/genesis-core';
import { useStaticAsset } from '@leagueplatform/asset-config';
import { EVENT_NAME, trackAnalyticsEvent } from '@leagueplatform/analytics';
import { getPDF, formatDate } from '../helper/helpers';
import { documentCenter } from '../document-center.config';
import * as CONSTANTS from '../utils/constant';
import { DOCUMENT_CENTER_ASSET_KEYS } from '../types/document-center.types';

export function ListingComponent(props: any) {
  const { docsList, isAllDocs, setIsLoading, setErrMsg, accessToken } = props;
  const config = documentCenter.getConfig();
  const { HIGHMARK_API_URL, useCurrentRegionUbkHeader } = config;
  const currentUbk: any = useCurrentRegionUbkHeader() || null;
  const ubkValue = currentUbk?.headers?.Brandubk || null;
  const pdfApiBaseUrl = `${HIGHMARK_API_URL}/digital/v1/members/claims`;

  const rightChevGray = useStaticAsset(
    DOCUMENT_CENTER_ASSET_KEYS.RIGHT_CHEV_GRAY,
  );
  const eobIcon = useStaticAsset(DOCUMENT_CENTER_ASSET_KEYS.EOB_ICON);

  const getPDFFile = (filePath: any) => {
    if (accessToken) {
      setIsLoading(true);
      getPDF(filePath, accessToken, ubkValue).then((res: any) => {
        setIsLoading(false);
        if (res.errMsg) {
          setErrMsg(true);
        }
      });
    }
  };

  const openNewWindow = (url: string) => {
    window.open(url, '_blank', 'noopener, noreferrer');
  };

  const getDetailValue = (item: any): string => {
    if (item?.letterTypeDescription) {
      return item.letterTypeDescription;
    }
    if (item?.guideName) {
      return item?.guideName;
    }
    return CONSTANTS.EXPLANATION_OF_BENEFITS;
  };

  const getDocumentTypeValue = (item: any): string => {
    if (item?.requestNumber) {
      return CONSTANTS.DETERMINATION_NOTICES_DOCUMENT;
    }
    if (item?.guideName) {
      return CONSTANTS.MA_WELCOME_GUIDES_DOCUMENT;
    }
    return CONSTANTS.EXPLANATION_OF_BENEFITS_DOCUMENT;
  };

  return (
    <>
      {docsList.map((item: any) => {
        let fileKey: string;
        let allDocsScreen: string;
        let pdfUrl: string;
        if (item.claimNumber) {
          fileKey = item.claimNumber;
        } else if (item.requestNumber) {
          fileKey = `${item.requestNumber}-${item.letterTypeDescription}`;
        } else {
          fileKey = item.documentKey;
        }

        if (item.claimNumber) {
          pdfUrl = `${pdfApiBaseUrl}/eob/file?claimNumber=${item.claimNumber}&documentKey=${item.documentKey}`;
        } else if (item.documentType === 'other') {
          pdfUrl = `${pdfApiBaseUrl}/um-letters/file?documentKey=${item.documentKey}`;
        } else {
          pdfUrl = '';
        }

        if (item.claimNumber) {
          allDocsScreen = CONSTANTS.EXPLANATION_OF_BENEFITS;
        } else if (item.documentType === 'welcome') {
          allDocsScreen = CONSTANTS.WELCOME_GUIDES;
        } else {
          allDocsScreen = CONSTANTS.DETERMINATION_NOTICES;
        }

        const screenName = isAllDocs
          ? allDocsScreen
          : CONSTANTS.DOCUMENT_CENTER;
        return (
          <TextAction
            key={fileKey}
            onClick={() => {
              if (item.documentType === 'welcome') {
                openNewWindow(item.documentKey);
              } else {
                getPDFFile(pdfUrl);
              }
              trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
                detail: getDetailValue(item),
                product_area: CONSTANTS.DOCUMENT_CENTER,
                sub_product_area: isAllDocs
                  ? CONSTANTS.ALL_DOCUMENTS
                  : CONSTANTS.RECENT_DOCUMENTS,
                document_type: getDocumentTypeValue(item),
                screen_name: screenName,
                path: window.location.pathname,
                event_author: CONSTANTS.ENGEN,
              });
            }}
            className="card-action"
            css={{
              display: 'block',
              cursor: 'pointer',
              background: '#fff',
              '&:hover': {
                textDecoration: 'none',
                outline: 'none',
              },
              '&:focus': { outline: '0 !important' },
              marginTop: '24px',
            }}
          >
            <Card.Elevated
              className="card-elevated"
              css={{
                border: '1px solid #E0E3E6',
                alignItems: 'center',
                padding: '24px',
                background: '#fff',
                backgroundColor: '#fff',
                boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.08)',
                borderRadius: '9px',
                '&:hover': {
                  boxShadow: '0 0 0 2px #0066B1FF',
                },
              }}
              imagePosition={{ '@initial': 'end' }}
            >
              <Card.Image
                image={rightChevGray as string}
                imageAlt="Arrow"
                css={{
                  height: '24px',
                  width: '24px',
                  '@mobile': {
                    height: '16px',
                    width: '16px',
                  },
                }}
              />
              <Card.Section
                padding={{
                  '@initial': '$none',
                  '@mobile': '$none',
                  '@tablet': '$none',
                  '@desktop': '$none',
                }}
              >
                <ParagraphText
                  css={{
                    fontSize: '16px',
                    fontFamily: `${CONSTANTS.FONT_ROBOTO}`,
                    lineHeight: '24px',
                    color: '#5A656D',
                    fontWeight: 400,
                  }}
                >
                  {formatDate(item.createDate)}
                </ParagraphText>
                <Box css={{ display: 'flex', margin: '12px 0' }}>
                  <img src={eobIcon as string} width="16px" alt="icon" />
                  <HeadingText
                    size="sm"
                    level="4"
                    css={{
                      fontSize: '12px',
                      lineHeight: '16px',
                      fontFamily: `${CONSTANTS.FONT_ROBOTO}`,
                      fontWeight: 700,
                      marginLeft: '12px',
                      letterSpacing: '0.6px',
                      textTransform: 'uppercase',
                      color: '#2C3135',
                    }}
                  >
                    {item.letterTypeDescription
                      ? item.letterTypeDescription
                      : CONSTANTS.EXPLANATION_OF_BENEFITS}
                  </HeadingText>
                </Box>
                <ParagraphText
                  css={{
                    fontSize: '16px',
                    margin: '5px 0',
                    fontFamily: `${CONSTANTS.FONT_ROBOTO}`,
                    color: '#5A656D',
                    lineHeight: '24px',
                    fontWeight: 400,
                    '@mobile': { color: '#2C3135' },
                  }}
                >
                  {item?.memberName && `${CONSTANTS.FOR}: ${item.memberName}`}
                </ParagraphText>
              </Card.Section>
            </Card.Elevated>
          </TextAction>
        );
      })}
    </>
  );
}
